import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Tabs,
  Table,
  Tag,
  Image,
  Empty,
  Calendar,
  Space,
  Typography,
  Button,
  Spin,
  Select,
  Slider,
  Row,
  Col,
  Alert
} from 'antd';
import { ArrowLeftOutlined, ShopOutlined, FilterOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/ru';
import { API_BASE_URL } from '../services/api';

dayjs.locale('ru');

const { Text } = Typography;

interface MenuItem {
  id: number;
  name: string;
  description?: string;
  category: string;
  weight: number;
  calories?: number | null;
  price: number;
  imageUrl?: string;
  thumbnailUrl?: string;
}

interface Supplier {
  id: number;
  companyName: string;
  inn: string;
  contactName: string;
  contactPhone: string;
  contactEmail?: string | null;
  contactPosition?: string | null;
}

interface WeeklyMenuEntry {
  id: number;
  weekStartDate: string;
  weekEndDate: string;
  menuItems: Array<{
    id: number;
    menuItemId: number;
    specificDate?: string | null;
    dayOfWeek?: string | null;
    isAvailable: boolean;
    menuItem: MenuItem;
  }>;
}

const categoryMap: { [key: string]: string } = {
  soup: 'Супы',
  main_course: 'Основные блюда',
  salad: 'Салаты',
  bakery: 'Выпечка',
  dessert: 'Десерты',
  beverage: 'Напитки',
  snack: 'Закуски',
  breakfast: 'Завтраки',
  side_dish: 'Гарниры',
  other: 'Прочее'
};

const formatDishCount = (count: number) => {
  const mod100 = count % 100;
  if (mod100 >= 11 && mod100 <= 14) return `${count} блюд`;
  const mod10 = count % 10;
  if (mod10 === 1) return `${count} блюдо`;
  if (mod10 >= 2 && mod10 <= 4) return `${count} блюда`;
  return `${count} блюд`;
};

/** Позиции из недельного плана на дату (без подстановки полного каталога). */
function computeWeeklyPlanItemsForDate(
  date: Dayjs,
  weeklyMenus: WeeklyMenuEntry[],
  filteredItems: MenuItem[]
): MenuItem[] {
  if (!weeklyMenus.length) {
    return [];
  }

  const targetMenu = weeklyMenus.find((menu) => {
    const start = dayjs(menu.weekStartDate).startOf('day');
    const end = dayjs(menu.weekEndDate).startOf('day');
    return (
      (date.isSame(start, 'day') || date.isAfter(start, 'day')) &&
      (date.isSame(end, 'day') || date.isBefore(end, 'day'))
    );
  });

  if (!targetMenu) {
    return [];
  }

  const dateKey = date.format('YYYY-MM-DD');
  const dayOfWeek = dayjs(date.valueOf()).locale('en').format('dddd').toLowerCase();

  const planItems = (targetMenu.menuItems || [])
    .filter((entry) => {
      if (entry.isAvailable === false) return false;
      if (entry.specificDate) {
        return dayjs(entry.specificDate).format('YYYY-MM-DD') === dateKey;
      }
      if (entry.dayOfWeek) {
        return entry.dayOfWeek === dayOfWeek;
      }
      return false;
    })
    .map((entry) => entry.menuItem)
    .filter(Boolean) as MenuItem[];

  return planItems.filter((dish) => filteredItems.some((filtered) => filtered.id === dish.id));
}

const SupplierMenuPreview: React.FC = () => {
  const { supplierId } = useParams<{ supplierId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [supplier, setSupplier] = useState<Supplier | null>(null);
  const [allItems, setAllItems] = useState<MenuItem[]>([]);
  const [weeklyMenus, setWeeklyMenus] = useState<WeeklyMenuEntry[]>([]);
  const [weeklyLoading, setWeeklyLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  /** Месяц, для которого загружены все пересекающиеся недельные меню (сетка календаря). */
  const [visibleMonth, setVisibleMonth] = useState<Dayjs>(() => dayjs().startOf('month'));
  const [viewMode, setViewMode] = useState<'all' | 'weekly'>('all');

  // Фильтры
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 30000]);
  const [maxPrice, setMaxPrice] = useState(30000);
  const [caloriesRange, setCaloriesRange] = useState<[number, number]>([0, 2000]);
  const [showFilters, setShowFilters] = useState(false);

  const resolveImageUrl = React.useCallback((path?: string | null) => {
    if (!path) return undefined;
    return path.startsWith('http') ? path : `${API_BASE_URL}${path}`;
  }, []);

  /** Все недельные меню, пересекающие сетку календаря для данного месяца (включая «хвосты» недель). */
  const loadWeeklyMenusForCalendarMonth = React.useCallback(
    async (monthAnchor: Dayjs) => {
      if (!supplierId) return;
      setWeeklyLoading(true);
      try {
        const fetchStart = monthAnchor.startOf('month').startOf('week');
        const fetchEnd = monthAnchor.endOf('month').endOf('week');

        const params = new URLSearchParams({
          supplierId,
          startDate: fetchStart.format('YYYY-MM-DD'),
          endDate: fetchEnd.format('YYYY-MM-DD')
        });

        const response = await fetch(`${API_BASE_URL}/api/menu/weekly?${params.toString()}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setWeeklyMenus(Array.isArray(data) ? data : []);
        } else {
          setWeeklyMenus([]);
        }
      } catch (error) {
        console.error('Load weekly menu error:', error);
        setWeeklyMenus([]);
      } finally {
        setWeeklyLoading(false);
      }
    },
    [supplierId]
  );

  const loadSupplierInfo = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/suppliers/${supplierId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setSupplier(data);
      } else if (response.status === 404) {
        setSupplier({ id: 0, companyName: 'Поставщик не найден', inn: '', contactName: '', contactPhone: '' });
      }
    } catch (error) {
      console.error('Load supplier error:', error);
      setSupplier({ id: 0, companyName: 'Ошибка загрузки', inn: '', contactName: '', contactPhone: '' });
    }
  };

  const loadAllItems = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/menu/items?supplierId=${supplierId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setAllItems(data);

        if (data.length > 0) {
          const maxItemPrice = Math.max(...data.map((item: MenuItem) => item.price));
          setMaxPrice(Math.ceil(maxItemPrice * 1.1));
          setPriceRange([0, maxItemPrice]);
        }
      }
    } catch (error) {
      console.error('Load items error:', error);
    } finally {
      setLoading(false);
    }
  };

  const visibleMonthKey = visibleMonth.format('YYYY-MM');

  useEffect(() => {
    loadSupplierInfo();
  }, [supplierId]);

  useEffect(() => {
    loadAllItems();
  }, [supplierId]);

  useEffect(() => {
    loadWeeklyMenusForCalendarMonth(visibleMonth);
  }, [supplierId, visibleMonthKey, loadWeeklyMenusForCalendarMonth]);

  const handleWeeklyCalendarSelect = (date: Dayjs) => {
    setSelectedDate(date);
    setVisibleMonth(date.startOf('month'));
  };

  const handleWeeklyPanelChange = (date: Dayjs) => {
    setVisibleMonth(date.startOf('month'));
  };

  const filteredItems = useMemo(() => {
    return allItems.filter((item) => {
      if (selectedCategories.length > 0 && !selectedCategories.includes(item.category)) {
        return false;
      }
      if (item.price < priceRange[0] || item.price > priceRange[1]) {
        return false;
      }
      // Если калории не указаны — не фильтруем по калориям.
      if (typeof item.calories === 'number') {
        if (item.calories < caloriesRange[0] || item.calories > caloriesRange[1]) {
          return false;
        }
      }
      return true;
    });
  }, [allItems, selectedCategories, priceRange, caloriesRange]);

  const weeklyItems = useMemo(
    () => computeWeeklyPlanItemsForDate(selectedDate, weeklyMenus, filteredItems),
    [selectedDate, weeklyMenus, filteredItems]
  );

  const resetFilters = () => {
    setSelectedCategories([]);
    setPriceRange([0, maxPrice]);
    setCaloriesRange([0, 2000]);
  };

  const columns: any[] = [
    {
      title: 'Фото',
      key: 'image',
      width: 100,
      render: (record: MenuItem) => {
        const previewSrc = resolveImageUrl(record.thumbnailUrl || record.imageUrl);
        const fullSrc = resolveImageUrl(record.imageUrl);

        if (previewSrc) {
          return (
            <Image
              src={previewSrc}
              alt={record.name}
              width={80}
              height={60}
              style={{ objectFit: 'cover', borderRadius: 4 }}
              preview={fullSrc ? { src: fullSrc } : false}
            />
          );
        }

        return (
          <div
            style={{
              width: 80,
              height: 60,
              background: '#f0f0f0',
              borderRadius: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Text type="secondary" style={{ fontSize: 11 }}>
              Нет фото
            </Text>
          </div>
        );
      }
    },
    {
      title: 'Название',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: MenuItem, b: MenuItem) => a.name.localeCompare(b.name),
      render: (text: string, record: MenuItem) => (
        <div>
          <div>
            <strong>{text}</strong>
          </div>
          {record.description && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.description}
            </Text>
          )}
        </div>
      )
    },
    {
      title: 'Категория',
      dataIndex: 'category',
      key: 'category',
      filters: Object.entries(categoryMap).map(([value, text]) => ({ text, value })),
      onFilter: (value: any, record: MenuItem) => record.category === value,
      render: (category: string) => <Tag color="blue">{categoryMap[category] || category}</Tag>
    },
    {
      title: 'Вес',
      dataIndex: 'weight',
      key: 'weight',
      sorter: (a: MenuItem, b: MenuItem) => a.weight - b.weight,
      render: (weight: number) => `${weight} г`
    },
    {
      title: 'Калории',
      dataIndex: 'calories',
      key: 'calories',
      sorter: (a: MenuItem, b: MenuItem) => (a.calories ?? -1) - (b.calories ?? -1),
      render: (calories?: number | null) =>
        typeof calories === 'number' ? `${calories} ккал` : '—'
    },
    {
      title: 'Цена',
      dataIndex: 'price',
      key: 'price',
      sorter: (a: MenuItem, b: MenuItem) => a.price - b.price,
      render: (price: number) => `${price.toLocaleString('ru-RU')} ₽`
    }
  ];

  return (
    <div>
      <Button type="link" icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
        Назад
      </Button>
      <Card
        title={
          <Space>
            <ShopOutlined />
            <span>{supplier?.companyName || 'Поставщик'}</span>
          </Space>
        }
        extra={
          supplier?.inn && (
            <Tag color="purple" style={{ fontSize: 14 }}>
              ИНН: {supplier.inn}
            </Tag>
          )
        }
      >
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col xs={24} md={12} lg={8}>
            <Card size="small" title="Контактное лицо">
              <Space direction="vertical" size={4}>
                <div>
                  <Text>{supplier?.contactName || 'Не указано'}</Text>
                  {supplier?.contactPosition && (
                    <Text type="secondary" style={{ display: 'block' }}>
                      {supplier.contactPosition}
                    </Text>
                  )}
                </div>
                <Text type="secondary">{supplier?.contactPhone || 'Телефон не указан'}</Text>
                <Text type="secondary">{supplier?.contactEmail || 'Email не указан'}</Text>
              </Space>
            </Card>
          </Col>
        </Row>

        <Tabs
          activeKey={viewMode}
          onChange={(key) => setViewMode(key as 'all' | 'weekly')}
          items={[
            {
              key: 'all',
              label: 'Все блюда',
              children: (
                <Card
                  size="small"
                  title={<Space><FilterOutlined />Фильтры</Space>}
                  extra={
                    <Button type="link" onClick={() => setShowFilters((prev) => !prev)}>
                      {showFilters ? 'Скрыть фильтры' : 'Показать фильтры'}
                    </Button>
                  }
                >
                  {showFilters && (
                    <Row gutter={[16, 16]}>
                      <Col xs={24} sm={12} md={8}>
                        <Select
                          mode="multiple"
                          allowClear
                          placeholder="Категории"
                          style={{ width: '100%' }}
                          value={selectedCategories}
                          onChange={setSelectedCategories}
                          options={Object.entries(categoryMap).map(([value, label]) => ({ value, label }))}
                        />
                      </Col>
                      <Col xs={24} sm={12} md={8}>
                        <div>
                          <Text>Цена</Text>
                          <Slider
                            range
                            min={0}
                            max={maxPrice}
                            value={priceRange}
                            onChange={(value) => setPriceRange(value as [number, number])}
                          />
                        </div>
                      </Col>
                      <Col xs={24} sm={12} md={8}>
                        <div>
                          <Text>Калории</Text>
                          <Slider
                            range
                            min={0}
                            max={2000}
                            step={50}
                            value={caloriesRange}
                            onChange={(value) => setCaloriesRange(value as [number, number])}
                          />
                        </div>
                      </Col>
                      <Col span={24}>
                        <Button onClick={resetFilters}>Сбросить фильтры</Button>
                      </Col>
                    </Row>
                  )}

                  <Table
                    loading={loading}
                    columns={columns}
                    dataSource={filteredItems}
                    rowKey="id"
                    pagination={{ pageSize: 10 }}
                  />
                </Card>
              )
            },
            {
              key: 'weekly',
              label: 'Меню по дням',
              children: (
                <Spin spinning={weeklyLoading}>
                  {weeklyMenus.length === 0 ? (
                    <>
                      <Alert
                        type="info"
                        showIcon
                        message="Поставщик пока не опубликовал меню по дням"
                        style={{ marginBottom: 16 }}
                      />
                      <Row gutter={16}>
                        <Col xs={24} md={12}>
                          <Calendar
                            fullscreen={false}
                            value={selectedDate}
                            onSelect={handleWeeklyCalendarSelect}
                            onPanelChange={handleWeeklyPanelChange}
                            dateFullCellRender={(value) => {
                              return (
                                <div style={{ padding: 4 }}>
                                  <div
                                    style={{
                                      borderRadius: 6,
                                      padding: '4px 6px',
                                      background: value.isSame(selectedDate, 'day') ? '#e6f7ff' : undefined,
                                      border: value.isSame(selectedDate, 'day') ? '1px solid #1890ff' : undefined
                                    }}
                                  >
                                    <div>{value.date()}</div>
                                  </div>
                                </div>
                              );
                            }}
                          />
                        </Col>
                        <Col xs={24} md={12}>
                          <Card
                            size="small"
                            title={`Блюда на ${selectedDate.format('DD.MM.YYYY')}`}
                            extra={<Tag color="blue">{selectedDate.format('dddd')}</Tag>}
                          >
                            <Empty description="На выбранную дату блюд нет" />
                          </Card>
                        </Col>
                      </Row>
                    </>
                  ) : (
                    <Row gutter={16}>
                      <Col xs={24} md={12}>
                        <Calendar
                          fullscreen={false}
                          value={selectedDate}
                          onSelect={handleWeeklyCalendarSelect}
                          onPanelChange={handleWeeklyPanelChange}
                          dateFullCellRender={(value) => {
                            const itemsForCell = computeWeeklyPlanItemsForDate(value, weeklyMenus, filteredItems);
                            const hasItems = itemsForCell.length > 0;
                            return (
                              <div style={{ padding: 4 }}>
                                <div
                                  style={{
                                    borderRadius: 6,
                                    padding: '4px 6px',
                                    background: value.isSame(selectedDate, 'day') ? '#e6f7ff' : undefined,
                                    border: value.isSame(selectedDate, 'day') ? '1px solid #1890ff' : undefined
                                  }}
                                >
                                  <div>{value.date()}</div>
                                  {hasItems && (
                                    <Tag color="green" style={{ marginTop: 4 }}>
                                      {formatDishCount(itemsForCell.length)}
                                    </Tag>
                                  )}
                                </div>
                              </div>
                            );
                          }}
                        />
                      </Col>
                      <Col xs={24} md={12}>
                        <Card
                          size="small"
                          title={`Блюда на ${selectedDate.format('DD.MM.YYYY')}`}
                          extra={<Tag color="blue">{selectedDate.format('dddd')}</Tag>}
                        >
                          {weeklyItems.length === 0 ? (
                            <Empty description="На выбранную дату блюд нет" />
                          ) : (
                            <div>
                              {weeklyItems.map((dish) => (
                                <div
                                  key={dish.id}
                                  style={{
                                    display: 'flex',
                                    gap: 16,
                                    padding: '12px 0',
                                    borderBottom: '1px solid #f0f0f0'
                                  }}
                                >
                                  <Image
                                    width={72}
                                    height={54}
                                    style={{ objectFit: 'cover', borderRadius: 6, flexShrink: 0 }}
                                    src={resolveImageUrl(dish.thumbnailUrl || dish.imageUrl)}
                                  />
                                  <div style={{ minWidth: 0 }}>
                                    <div>
                                      <Text strong>{dish.name}</Text>
                                    </div>
                                    <Space size="small" wrap style={{ marginTop: 8 }}>
                                      {dish.category && (
                                        <Tag color="blue">{categoryMap[dish.category] || dish.category}</Tag>
                                      )}
                                      {dish.weight > 0 && <Tag>{dish.weight} г</Tag>}
                                      {dish.calories > 0 && <Tag>{dish.calories} ккал</Tag>}
                                      <Tag color="green">{dish.price.toLocaleString('ru-RU')} ₽</Tag>
                                    </Space>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </Card>
                      </Col>
                    </Row>
                  )}
                </Spin>
              )
            }
          ]}
        />
      </Card>
    </div>
  );
};

export default SupplierMenuPreview;

